import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { ScheduleI } from '~src/interfaces/schedule.interface';
import { Segment, SegmentType } from '~src/interfaces/segment.interface';
import { SurveySubjectTypes, SurveyUseCase } from '~src/interfaces/surveys.interfaces';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
import { Survey } from '~src/modules/surveys/interfaces';
import {
  ChipsError,
  HeaderContainer,
  PeopleSectionHeader,
  SchedulesItemSection,
  SectionLine,
  TopicDescription,
} from '~src/modules/surveys/components/schedules/scheduleItem/templates/scheduleTemplate.sc';

import CheckBoxSection from './CheckBoxSection';
import PeopleChips from './customInputs/PeopleChips';
import ToggleBoxSection from './ToggleBoxSection';
import { useSelector } from 'react-redux';
import { StoreI } from '~src/rootReducer';
import { SessionUserI } from '~src/interfaces/session.types';

interface Props {
  survey: Survey;
  error: boolean;
  selectedPeople: Segment[];
  schedule: ScheduleI;
  oldSchedule: ScheduleI;
  deleteError: (err: string) => void;
  getAskedPeople: (people: Segment[], key: string) => void;
  useCaseKey: string;
  onChangeBox: (active: boolean) => void;
  changeFlag: (value: boolean, key: keyof ScheduleI) => void;
  setInputRef: ((node: HTMLInputElement) => void) | null;
  onChangeManagerAnswerVisible?: (isManagerAnonymity: boolean) => void;
}

const AskSection: FC<Props> = (props: Props) => {
  const { useCase, subject } = props.survey;
  const { error } = props;
  const { t } = useTranslation();
  const useCaseKey =
    useCase === SurveyUseCase.TopDown ? '.top_down' : subject === SurveySubjectTypes.General ? '.pulse' : '';

  const sessionUser = useSelector<StoreI, SessionUserI | undefined>((s) => s.user.session.user ?? undefined);
  return (
    <SchedulesItemSection>
      <SectionLine>
        <PeopleSectionHeader>
          <HeaderContainer data-translation={`x.surveys.schedule.asksection.header${useCaseKey}`}>
            {t(`x.surveys.schedule.asksection.header${useCaseKey}`)}
            <TopicDescription>
              <HoverInfoIcon
                infoText={{
                  key: `x.surveys.schedule.asksection.header.info_box.asksection.${subject}${props.useCaseKey}`,
                  txt: t(
                    `x.surveys.schedule.asksection.header.info_box.asksection.${subject}${props.useCaseKey}`,
                  ),
                }}
              />
            </TopicDescription>
          </HeaderContainer>
          {error && (
            <ChipsError data-translation="x.surveys.schedule.asksection.errormessage">
              {t('x.surveys.schedule.asksection.errormessage')}
            </ChipsError>
          )}
        </PeopleSectionHeader>
      </SectionLine>
      <SectionLine $noPadding>
        <PeopleChips
          placeHolderKey={`x.surveys.schedule.input.ask.placeholder.${useCase || subject}`}
          setInputRef={props.setInputRef}
          fetchSuggestions={fetchSearchSegments}
          chipsName="ask"
          selectedPeople={props.schedule.ask || []}
          onChangePeople={props.getAskedPeople}
          error={error}
          deleteError={props.deleteError}
        />
      </SectionLine>
      <SectionLine>
        {useCase === SurveyUseCase.TopDown ? (
          <CheckBoxSection
            useCase={useCase}
            subject={subject}
            schedule={props.schedule}
            withToggleBox
            title={t(`x.surveys.schedule.asksection.checkbox.${useCase}`)}
            titleKey={`x.surveys.schedule.asksection.checkbox.${useCase}`}
            toggleBoxTitle={t(
              `x.surveys.schedule.aboutsection.togglebox.title.${subject}${props.useCaseKey}`,
            )}
            onChangeToggleBox={props.onChangeBox}
            active={props.schedule.anonymous}
            changeFlag={props.changeFlag}
            oldSchedule={props.schedule}
          />
        ) : (
          <ToggleBoxSection
            t={t}
            sessionUser={sessionUser}
            oldSchedule={props.oldSchedule}
            schedule={props.schedule}
            fetchSearchSegments={(value, cancelToken) =>
              fetchSearchSegments(value, cancelToken, [
                SegmentType.Anonymous,
                SegmentType.Company,
                SegmentType.Group,
                SegmentType.User,
              ])
            }
            onChangeToggleBox={props.onChangeBox}
            title={t(`x.surveys.schedule.aboutsection.togglebox.title.${subject}${props.useCaseKey}`)}
            active={props.schedule.anonymous}
            data-translation={`x.surveys.schedule.aboutsection.togglebox.title.${subject}${props.useCaseKey}`}
            subject={subject}
            useCase={useCase}
            scope={props.survey.scope}
            changeFlag={props.changeFlag}
            getAskedPeople={props.getAskedPeople}
            onChangeManagerAnswerVisible={props.onChangeManagerAnswerVisible}
          />
        )}
      </SectionLine>
    </SchedulesItemSection>
  );
};

export default AskSection;
